import type { Signals, SignalModuleId, ModuleMeta, CollectResult } from '@fraudeai/schema';
import { SCHEMA_VERSION } from '@fraudeai/schema';
import { errorMessage, now, withTimeout, yieldToBrowser } from './util/safe.js';
import { hash } from './util/hash.js';

export interface CollectOptions {
  /** Таймаут по умолчанию на один модуль. */
  moduleTimeoutMs?: number;
  /** Не запускать перечисленные модули. */
  skip?: SignalModuleId[];
  /** Колбэк прогресса для UI. */
  onProgress?: (done: number, total: number, current: SignalModuleId) => void;
  /**
   * Same-origin URL скрипта service worker для cross-context проверки.
   * Без него контекст service worker помечается недоступным: blob-URL для
   * регистрации SW не годится, нужен настоящий файл на том же origin.
   */
  serviceWorkerUrl?: string;
  /**
   * Базовый адрес API. Нужен модулю внешних проб: он запрашивает сессию
   * и получает адреса STUN, HTTP/3 и зоны DNS. Без него пробы не запускаются.
   */
  apiBaseUrl?: string;
  /** Запросить камеру и снять opt-in признаки видеопотока. */
  requestMediaProbe?: boolean;
  /** Запросить местоположение и снять opt-in признаки GPS. */
  requestGeolocationProbe?: boolean;
}

export interface ModuleContext {
  /** Сигналы, собранные предыдущими фазами. Модуль фазы N видит фазы < N. */
  signals: Partial<Signals>;
  options: CollectOptions & { moduleTimeoutMs: number };
}

export interface CollectorModule<K extends SignalModuleId> {
  id: K;
  /** Модули одной фазы выполняются параллельно, фазы — последовательно. */
  phase: number;
  timeoutMs?: number;
  run(ctx: ModuleContext): Promise<Signals[K]> | Signals[K];
}

/** Дискриминированное объединение всех модулей — сохраняет связь id ↔ тип данных. */
export type AnyCollectorModule = { [K in SignalModuleId]: CollectorModule<K> }[SignalModuleId];

/** Хелпер только ради вывода типов: `defineModule({ id: 'canvas', ... })`. */
export function defineModule<K extends SignalModuleId>(m: CollectorModule<K>): CollectorModule<K> {
  return m;
}

const DEFAULT_TIMEOUT_MS = 4000;

function makeRunId(): string {
  const entropy = [
    Date.now(),
    now(),
    Math.random(),
    // crypto.randomUUID есть не везде, а нам достаточно неповторяемости запуска.
    globalThis.crypto?.getRandomValues
      ? Array.from(globalThis.crypto.getRandomValues(new Uint8Array(8))).join('')
      : Math.random(),
  ].join('|');
  return hash(entropy).slice(0, 16);
}

/**
 * Прогоняет все модули и собирает результат.
 *
 * Сбой или таймаут отдельного модуля фиксируется в метаданных и не прерывает
 * остальные: неполный отчёт полезнее отсутствующего, а сам факт падения
 * модуля — сигнал (в нормальном браузере эти API не падают).
 */
export async function runCollector(
  modules: AnyCollectorModule[],
  options: CollectOptions = {},
): Promise<CollectResult> {
  const startedAt = Date.now();
  const startMark = now();
  const moduleTimeoutMs = options.moduleTimeoutMs ?? DEFAULT_TIMEOUT_MS;

  const skip = new Set(options.skip ?? []);
  const active = modules.filter((m) => !skip.has(m.id));

  const ctx: ModuleContext = { signals: {}, options: { ...options, moduleTimeoutMs } };
  const meta: ModuleMeta[] = [];

  const phases = [...new Set(active.map((m) => m.phase))].sort((a, b) => a - b);
  let done = 0;

  for (const phase of phases) {
    const batch = active.filter((m) => m.phase === phase);

    await Promise.all(
      batch.map(async (module) => {
        options.onProgress?.(done, active.length, module.id);
        const t0 = now();
        try {
          const result = await withTimeout(
            Promise.resolve(module.run(ctx)),
            module.timeoutMs ?? moduleTimeoutMs,
            module.id,
          );
          // Присваивание через индекс: связь id ↔ тип уже проверена в defineModule.
          (ctx.signals as Record<string, unknown>)[module.id] = result;
          meta.push({
            id: module.id,
            ok: true,
            ms: Math.round(now() - t0),
            partial: hasNullFields(result),
          });
        } catch (error) {
          meta.push({
            id: module.id,
            ok: false,
            ms: Math.round(now() - t0),
            error: errorMessage(error),
          });
        } finally {
          done++;
          options.onProgress?.(done, active.length, module.id);
        }
      }),
    );

    await yieldToBrowser();
  }

  meta.sort((a, b) => a.id.localeCompare(b.id));

  return {
    schemaVersion: SCHEMA_VERSION,
    runId: makeRunId(),
    startedAt,
    ms: Math.round(now() - startMark),
    modules: meta,
    signals: ctx.signals,
  };
}

/** Есть ли в результате модуля недоступные сигналы — помечает отчёт как неполный. */
function hasNullFields(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return value === null;
  return Object.values(value as Record<string, unknown>).some((v) => v === null);
}
